/**
 * Copy deck for the document-plane customer steps (INTAKE-08 §1.2/§7 rules):
 *
 *   • FULL-SENTENCE keys — one message per key, at most ONE interpolation slot,
 *     never string-concatenated fragments (Hindi/Arabic word order differs, so a
 *     fragment join can never translate; the whole sentence lives in the key).
 *   • Customer tone: plain, warm, second person, no jargon, no blame, never a
 *     score or a threshold ("Let's try that again", not "Your photo failed").
 *   • Certification honesty: "verified"/"certified" are RESERVED words — the
 *     claim ceiling on-device is "Quality-checked on your device"; an uploaded
 *     file is labelled "Uploaded — lower evidence grade", and the two labels
 *     must NEVER look or read identically (honesty ladder, INTAKE-08 §1.1).
 *   • EN ships now; an HI/AR pack is the SAME key set with translated sentences
 *     (structure is pack-ready by construction — keys, not fragments).
 *
 * The evidence-grade labels are keyed exactly so VCIP-REG can retune the wording
 * without a code change (INTAKE-08 §A2: `capture.grade.live` / `capture.grade.upload`).
 */

export const DOCUMENT_STEPS_COPY = {
  // ── shared capture grade labels (the honesty ladder made visible) ──────────
  'droom.capture.grade.live': 'Quality-checked on device',
  'droom.capture.grade.upload': 'Uploaded — lower evidence grade',
  'droom.capture.grade.upload.chip': 'lower grade',
  'droom.capture.grade.upload.explain':
    'An uploaded copy carries a lower evidence grade — your verifier confirms your documents with you on the call.',

  // ── SELFIE step ────────────────────────────────────────────────────────────
  'droom.selfie.title': 'Take a clear selfie',
  'droom.selfie.sub': 'Face the camera in even light and keep your whole face inside the oval.',
  'droom.selfie.hint.lineup': 'Keep your whole face inside the oval.',
  'droom.selfie.hint.toodark': 'It looks a little dark — move somewhere brighter.',
  'droom.selfie.hint.toobright': 'It looks too bright — move away from direct light.',
  'droom.selfie.primer': 'We’ll use your front camera for your selfie.',
  'droom.selfie.done.title': 'Selfie captured',
  'droom.selfie.done.sub': 'Your selfie is saved securely — nothing else to do here.',

  // ── SELFIE_WITH_DOC step ───────────────────────────────────────────────────
  'droom.selfiedoc.title': 'Hold your document next to your face',
  'droom.selfiedoc.sub':
    'One photo shows you and your document together — keep your face in the oval and the document in the box.',
  'droom.selfiedoc.hint.lineup': 'Keep your face in the oval and your document in the box.',
  'droom.selfiedoc.hint.readable': 'Hold the document flat so every detail stays readable.',
  'droom.selfiedoc.primer':
    'We’ll use your front camera — have your PAN card, driving licence or passport ready in your hand. Please do NOT hold your Aadhaar card to the camera — it is never needed here.',
  'droom.selfiedoc.done.title': 'Photo captured',
  'droom.selfiedoc.done.sub': 'Your photo with your document is saved securely.',
  'droom.selfiedoc.unavailable':
    'This step isn’t available on this deployment yet — your verifier will take this photo with you on the call.',

  // ── shared selfie-family capture flow ──────────────────────────────────────
  'droom.capture.assessing': 'Checking quality on your device…',
  'droom.capture.uploading': 'Uploading securely…',
  'droom.capture.preview.ok': 'Looks clear — use this photo, or retake.',
  'droom.capture.retake.note': 'Let’s try that again — retakes are completely normal.',
  'droom.capture.camera.starting': 'Starting your camera…',
  'droom.capture.camera.checking': 'Checking camera access…',
  'droom.capture.camera.notready': 'The camera is still starting — give it a second and try again.',
  'droom.capture.fallback.oncall': 'Continue — take this photo with your verifier on the call',
  'droom.capture.uploadfailed': 'Upload failed — check your connection and try again.',

  // ── UPLOAD step (document plane) ───────────────────────────────────────────
  'droom.upload.title': 'Add your documents',
  'droom.upload.sub': 'Upload a clear photo of each document listed below.',
  'droom.upload.choose': 'Choose photo',
  'droom.upload.replaceNot': 'The first copy we receive is the one that counts — it can’t be replaced here.',
  'droom.upload.liveonly': 'This document is photographed live with your verifier on the call.',
  'droom.upload.required': 'Required',
  'droom.upload.optional': 'Optional',
  'droom.upload.progress': 'Uploading securely…',
  'droom.upload.notavailable.title': 'Document upload isn’t available here yet',
  'droom.upload.notavailable.body':
    'This deployment doesn’t support document upload yet — your documents will be checked with your verifier on the call.',
  'droom.upload.loading': 'Checking which documents we need…',
  'droom.upload.loaderror': 'We couldn’t load your document list. Please try again.',

  // ── rejection honesty (icon + plain reason + remedy, ONE sentence each) ────
  'droom.capture.reject.blur': 'That photo came out blurry — hold steady and try again.',
  'droom.capture.reject.dark': 'It’s a little dark — move to better light and try again.',
  'droom.capture.reject.bright': 'There’s a bright reflection — tilt away from the light and retake.',
  'droom.capture.reject.small': 'That photo is too small to read clearly — try a device with a sharper camera.',
  'droom.capture.reject.unreadable': 'That photo could not be processed — please try again.',
  'droom.upload.reject.file': 'That file won’t work — please upload a clear photo (JPG or PNG).',
  'droom.upload.reject.toolarge': 'That file is too large — please upload a photo under 8 MB.',
  'droom.upload.reject.blur': 'This photo looks blurry — a clearer one verifies faster.',
  'droom.upload.reject.dark': 'This photo looks dark — one taken in better light verifies faster.',
  'droom.upload.reject.bright': 'This photo looks washed out — one without glare verifies faster.',
  'droom.upload.reject.small': 'This photo is too small to read — a larger, clearer one verifies faster.',
  'droom.upload.reject.unreadable': 'We couldn’t read that file — please choose a clear photo (JPG or PNG).',
} as const;

export type DocumentStepsCopyKey = keyof typeof DOCUMENT_STEPS_COPY;

/** Resolve a copy key (EN pack; HI/AR packs swap the record, never the keys). */
export function copyText(key: DocumentStepsCopyKey): string {
  return DOCUMENT_STEPS_COPY[key];
}
